import { useEffect, useState, type FormEvent } from 'react';
import type { AdminPrice, ApiClient, Plan } from '../api';
import { formatDate, formatMoney, promptForReason, renderError } from './shared';

/**
 * What each plan costs in the account panel. Amounts are never edited: a new
 * price is added and the old one retired, so every past payment still points
 * at what it was actually charged.
 */
export function PricesView({ client }: { client: ApiClient }) {
  const [prices, setPrices] = useState<AdminPrice[] | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [form, setForm] = useState({ planKey: 'pro', intervalMonths: '1', amount: '', currency: 'PLN', key: '', sortOrder: '' });
  const [created, setCreated] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([client.listPrices(), client.getPlans()])
      .then(([priceList, planList]) => {
        if (cancelled) return;
        setPrices(priceList.prices);
        setPlans(planList.plans.filter((plan) => !plan.isDefault));
      })
      .catch((err) => !cancelled && setError(err));
    return () => {
      cancelled = true;
    };
  }, [client, tick]);

  const suggestedKey = `${form.planKey}-${form.intervalMonths === '12' ? 'yearly' : form.intervalMonths === '1' ? 'monthly' : `${form.intervalMonths}m`}-${new Date().getFullYear()}`;

  async function create(event: FormEvent) {
    event.preventDefault();
    const amountMinor = Math.round(Number(form.amount.replace(',', '.')) * 100);
    if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
      setError(new Error('Enter the gross amount, e.g. 29.00'));
      return;
    }
    const reason = await promptForReason('adding this price');
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      const price = await client.createPrice({
        key: form.key.trim() || suggestedKey,
        planKey: form.planKey,
        currency: form.currency.trim().toUpperCase(),
        amountMinor,
        intervalMonths: Number(form.intervalMonths),
        sortOrder: form.sortOrder ? Number(form.sortOrder) : undefined,
        reason,
      });
      setCreated(`${price.key}: ${formatMoney(price.amountMinor, price.currency)} — now on sale. Retire the price it replaces.`);
      setForm((current) => ({ ...current, amount: '', key: '', sortOrder: '' }));
      setTick((value) => value + 1);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function toggle(price: AdminPrice) {
    const reason = await promptForReason(price.active ? `retiring ${price.key}` : `putting ${price.key} back on sale`);
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      await client.setPriceActive(price.id, !price.active, reason);
      setTick((value) => value + 1);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">Billing</p>
          <h2>Prices</h2>
          <p className="muted">
            What the account panel sells. Gross amounts (VAT included). To change a price, add the new one, then retire the old one.
          </p>
        </div>
      </div>

      {error !== null && renderError(error)}
      {created && <div className="notice">{created}</div>}

      <div className="panel">
        {prices === null ? (
          <p className="muted">Loading…</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Key</th>
                <th>Plan</th>
                <th>Period</th>
                <th>Amount</th>
                <th>Payments</th>
                <th>Added</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {prices.map((price) => (
                <tr key={price.id} className={price.active ? '' : 'retired'}>
                  <td>
                    <code>{price.key}</code>
                  </td>
                  <td>{price.planName}</td>
                  <td>{price.intervalMonths === 12 ? 'year' : price.intervalMonths === 1 ? 'month' : `${price.intervalMonths} months`}</td>
                  <td>{formatMoney(price.amountMinor, price.currency)}</td>
                  <td>{price.payments ?? 0}</td>
                  <td>{formatDate(price.createdAt)}</td>
                  <td>{price.active ? <span className="tag active">on sale</span> : <span className="tag">retired</span>}</td>
                  <td>
                    <button className={price.active ? 'danger' : ''} disabled={busy} onClick={() => void toggle(price)}>
                      {price.active ? 'Retire' : 'Put back on sale'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <form className="panel stack" onSubmit={create}>
        <h2>Add a price</h2>
        <div className="form-grid">
          <label>
            Plan
            <select value={form.planKey} onChange={(event) => setForm({ ...form, planKey: event.target.value })}>
              {plans.map((plan) => (
                <option key={plan.key} value={plan.key}>
                  {plan.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Period
            <select value={form.intervalMonths} onChange={(event) => setForm({ ...form, intervalMonths: event.target.value })}>
              <option value="1">1 month</option>
              <option value="3">3 months</option>
              <option value="6">6 months</option>
              <option value="12">1 year</option>
            </select>
          </label>
          <label>
            Gross amount
            <input inputMode="decimal" placeholder="29.00" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} />
          </label>
          <label>
            Currency
            <input value={form.currency} maxLength={3} onChange={(event) => setForm({ ...form, currency: event.target.value })} />
          </label>
          <label>
            Key
            <input placeholder={suggestedKey} value={form.key} onChange={(event) => setForm({ ...form, key: event.target.value })} />
          </label>
          <label>
            Sort order
            <input inputMode="numeric" placeholder="0" value={form.sortOrder} onChange={(event) => setForm({ ...form, sortOrder: event.target.value })} />
          </label>
        </div>
        <p className="muted small">Keys are permanent and never reused. The new price goes on sale immediately.</p>
        <div>
          <button className="primary" type="submit" disabled={busy || !form.amount}>
            Add price
          </button>
        </div>
      </form>
    </>
  );
}
